import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom, map, of, throwError } from 'rxjs';
import { EventConfigResponseData } from 'src/app/@data-services/event-config/event-config.data-model';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { MixpanelService } from 'src/app/@services/mixpanel.service';
import { LanguageStore } from 'src/app/@store/language-store';
import { applyDocumentTitle } from 'src/app/@utils/apply-document-title';
import { applyTheme } from 'src/app/@utils/apply-theme';
import { environment } from 'src/environments/environment';
import { eventQrTargetUrl, resolveEventQr } from 'src/app/@utils/event-qr';
import { getEuEndpointsForHostname } from './eu-event-routing.config';
import {
  eventConfigRequestBody,
  isSubpathMode,
  resolveEventDomain,
} from 'src/app/@utils/base-path';

/** sessionStorage can throw on access (storage disabled); the resolver handles null. */
function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function appEventConfigInitializer() {
  const http = inject(HttpClient);
  const doc = inject(DOCUMENT);

  // Subdomain mode: identical to before (hostname / localhostDomain).
  // Subpath mode (/event/ on the console origin): explicit ?event= param.
  const eventDomain = resolveEventDomain({
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    search: window.location.search,
    localhostDomain: environment.localhostDomain,
    // sessionStorage: per-tab, never shared with the console's localStorage tokens
    storage: safeSessionStorage(),
  });
  if (eventDomain === null) {
    // No event in the URL (/event/ with no id): show the 404 page.
    const stateService = inject(EventConfigStateService);
    return (): Promise<void> => {
      stateService.setEventNotFound();
      return Promise.resolve();
    };
  }

  // Apply EU endpoint overrides if the current event is hosted in EU.
  // In subdomain mode eventDomain === hostname (unchanged); in subpath mode the
  // console host says nothing about the event, so route on the event domain.
  const euEndpoints = getEuEndpointsForHostname(
    window.location.hostname === 'localhost' &&
      !isSubpathMode(window.location.pathname)
      ? window.location.hostname
      : eventDomain,
    environment.euStage
  );
  if (euEndpoints) {
    environment.apiUrl = euEndpoints.apiUrl;
    environment.wsUrl = euEndpoints.wsUrl;
    environment.X_API_KEY = euEndpoints.xApiKey;
  }

  const apiUrl = environment.apiUrl;
  const eventConfigStateService = inject(EventConfigStateService);
  const languageStore = inject(LanguageStore);
  const mixpanelService = inject(MixpanelService);

  return (): Promise<void> =>
    firstValueFrom(
      http
        .post<EventConfigResponseData>(
          apiUrl + '/r1/getEventConfig',
          eventConfigRequestBody(eventDomain, window.location.pathname)
        )
        .pipe(
          map((response) => {
            if (!response?.success || !response.data) {
              eventConfigStateService.setEventNotFound();
              return;
            }
            eventConfigStateService.setEventIdentifier(
              response.data.EventIdentifier
            );
            eventConfigStateService.setEventConfigData(response.data);
            // No uploaded QR image: generate one for this event's page.
            const info = response.data.Information;
            if (!info?.Images?.EventQR?.trim()) {
              resolveEventQr(
                info?.Images?.EventQR,
                eventQrTargetUrl(doc.location, response.data.EventIdentifier)
              )
                .then((qr) =>
                  eventConfigStateService.setEventConfigData({
                    ...response.data,
                    Information: {
                      ...info,
                      Images: { ...info?.Images, EventQR: qr },
                    },
                  })
                )
                .catch(() => undefined);
            }
            eventConfigStateService.setSponsors(response.data.Sponsors);
            eventConfigStateService.setFeatureConfigData(
              response.data.Features
            );
            languageStore.setAvailableLanguages(
              response.data.SupportedLanguages
            );
            languageStore.setOriginalLanguageCode(
              response.data.OriginalLanguageCode
            );
            applyTheme(response.data.Themes);
            applyDocumentTitle(response.data.Information.EventNameDisplay);
            mixpanelService.registerEvent(
              response.data.EventIdentifier,
              eventDomain
            );
          }),
          // 404 = no config row for this event: show the 404 page. Other errors still fail.
          catchError((err: unknown) => {
            if (err instanceof HttpErrorResponse && err.status === 404) {
              eventConfigStateService.setEventNotFound();
              return of(undefined);
            }
            return throwError(() => err);
          })
        )
    );
}
